import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { AuthorizationFacade } from '@elite/authentication/domain';
import { EliteListPageImports } from '@elite/shared/ui-patterns';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCar, lucideUser, lucideUsers } from '@ng-icons/lucide';
import { filter, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/** One quick-action card. `key` is the test id suffix and the `@for` track. */
interface LandingAction {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  readonly icon: string;
  readonly route: string;
}

@Component({
  selector: 'elite-landing',
  imports: [NgIcon, RouterLink, EliteListPageImports],
  providers: [provideIcons({ lucideCar, lucideUser, lucideUsers })],
  templateUrl: './landing.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingComponent {
  readonly appName = environment.appName;

  private authorizationFacade = inject(AuthorizationFacade);

  /**
   * Each card waits for permissions to be *fully* loaded before asking about one, rather
   * than asking immediately and getting `false` from a store that has not answered yet —
   * which would flash the whole dashboard empty on every sign-in.
   */
  private can(category: Parameters<AuthorizationFacade['hasPermission$']>[0]) {
    return toSignal(
      this.authorizationFacade.fullyLoaded$.pipe(
        filter((loaded) => loaded === true),
        switchMap(() => this.authorizationFacade.hasPermission$(category, 'View')),
      ),
    );
  }

  readonly canViewRides = this.can('ride');
  readonly canViewDrivers = this.can('drivers');
  readonly canViewUsers = this.can('users');
  readonly canViewCustomers = this.can('customers');

  /**
   * The cards are data rather than four near-identical blocks of markup, because they were
   * four near-identical blocks of markup: the same card, the same hover treatment and the
   * same three lines of copy, repeated with one word changed, each behind its own `@if`.
   */
  private readonly actions: readonly (LandingAction & { readonly visible: () => boolean | undefined })[] = [
    {
      key: 'rides',
      label: 'Manage Rides',
      description: 'View and manage all ride requests and bookings',
      icon: 'lucideCar',
      route: '/ride',
      visible: this.canViewRides,
    },
    {
      key: 'drivers',
      label: 'Drivers',
      description: 'Manage driver profiles and assignments',
      icon: 'lucideUsers',
      route: '/drivers',
      visible: this.canViewDrivers,
    },
    {
      key: 'customers',
      label: 'Customers',
      description: 'Manage customer profiles and bookings',
      icon: 'lucideUser',
      route: '/customers',
      visible: this.canViewCustomers,
    },
    {
      key: 'users',
      label: 'Users',
      description: 'Manage user accounts and permissions',
      icon: 'lucideUser',
      route: '/users',
      visible: this.canViewUsers,
    },
  ];

  readonly visibleActions = computed<readonly LandingAction[]>(() => this.actions.filter((a) => a.visible()));
}
